/**
 * Feature hints: small, non-blocking "what's new" tips anchored to new UI.
 *
 * - "Don't show again" hides a hint permanently (localStorage, per user).
 * - "Remind me later" hides it for the current browser session.
 * - "Hide all tips" turns every hint off for this user.
 *
 * Storage access is wrapped: private windows / blocked storage just means hints
 * show again, never an error.
 */

export type HintPlacement = 'right' | 'bottom' | 'top' | 'left';

export interface FeatureHint {
  id: string;
  /** Pathname test; hints only show on matching routes. */
  route: (pathname: string) => boolean;
  /** Anchor element; the hint is skipped while the anchor is missing or off-screen. */
  selector: string;
  title: string;
  body: string;
  placement: HintPlacement;
}

const isInvestorDetail = (p: string) => /^\/investors\/\d+/.test(p);

/** Ordered by priority: at most one hint is visible at a time. */
export const FEATURE_HINTS: FeatureHint[] = [
  {
    id: 'investors-nav-v1',
    route: (p) => !p.startsWith('/investors'),
    selector: '[data-hint="investors-nav"]',
    title: 'New: Investors',
    body: 'Follow notable investors like Semper Augustus and Greenhaven Road. Every holding in their latest 13F is checked against insider buying each morning.',
    placement: 'right',
  },
  {
    id: 'dashboard-overlap-v1',
    route: (p) => p === '/dashboard',
    selector: '[data-hint="investor-overlap"]',
    title: 'Insiders buying what your investors own',
    body: 'Open-market insider purchases in stocks your followed investors hold, refreshed daily. Until you follow anyone, it uses the featured list.',
    placement: 'bottom',
  },
  {
    id: 'investors-follow-v1',
    route: (p) => p === '/investors',
    selector: '[data-hint="investor-follow"]',
    title: 'Follow an investor',
    body: 'Following adds them to your dashboard feed and turns on email alerts for new 13F filings and insider buys in their holdings.',
    placement: 'bottom',
  },
  {
    id: 'investors-add-v1',
    route: (p) => p === '/investors',
    selector: '[data-hint="investor-add"]',
    title: 'Track anyone who files a 13F',
    body: 'Search SEC EDGAR by fund or manager name. We load two years of history and start cross-checking it right away.',
    placement: 'top',
  },
  {
    id: 'investor-lookthrough-v1',
    route: isInvestorDetail,
    selector: '[data-hint="lookthrough-tab"]',
    title: 'The portfolio as one company',
    body: 'See the whole book consolidated per $100 of sales and compared with the S&P 500, including look-through growth, so cheap-but-shrinking portfolios stand out.',
    placement: 'bottom',
  },
  {
    id: 'investor-changes-v1',
    route: isInvestorDetail,
    selector: '[data-hint="changes-tab"]',
    title: 'What changed this quarter',
    body: 'New positions, adds, trims and exits, with insider buying flagged next to each name.',
    placement: 'bottom',
  },
  {
    id: 'investor-alerts-v1',
    route: isInvestorDetail,
    selector: '[data-hint="investor-alerts"]',
    title: 'Alerts for this investor',
    body: 'Choose which emails you get: insider buys in their holdings, new 13F filings, or both.',
    placement: 'left',
  },
  {
    id: 'company-held-by-v1',
    route: (p) => p.startsWith('/companies/'),
    selector: '[data-hint="held-by"]',
    title: 'Held by notable investors',
    body: 'Which tracked investors own this stock, how much, and whether they added or trimmed last quarter.',
    placement: 'top',
  },
  {
    id: 'alerts-investors-v1',
    route: (p) => p === '/alerts',
    selector: '[data-hint="investor-alerts-card"]',
    title: 'Investor alerts live here too',
    body: 'Manage email alerts for every investor you follow in one place, next to your transaction alerts.',
    placement: 'bottom',
  },
];

interface HintState {
  dismissed: string[];
  allOff: boolean;
}

const storageKey = (userKey: string) => `avasar-feature-hints:${userKey}`;
const SESSION_KEY = 'avasar-feature-hints-snoozed';

const readJson = <T>(storage: () => Storage, key: string, fallback: T): T => {
  try {
    const raw = storage().getItem(key);
    return raw ? ({ ...fallback, ...JSON.parse(raw) } as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (storage: () => Storage, key: string, value: unknown) => {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable: hints simply show again next time.
  }
};

const local = () => window.localStorage;
const session = () => window.sessionStorage;

export const getHintState = (userKey: string): HintState =>
  readJson<HintState>(local, storageKey(userKey), { dismissed: [], allOff: false });

const getSnoozed = (): string[] =>
  readJson<{ ids: string[] }>(session, SESSION_KEY, { ids: [] }).ids;

export const isHintHidden = (userKey: string, id: string): boolean => {
  const state = getHintState(userKey);
  return state.allOff || state.dismissed.includes(id) || getSnoozed().includes(id);
};

export const dismissHint = (userKey: string, id: string): void => {
  const state = getHintState(userKey);
  if (!state.dismissed.includes(id)) state.dismissed.push(id);
  writeJson(local, storageKey(userKey), state);
};

export const snoozeHint = (id: string): void => {
  const ids = getSnoozed();
  if (!ids.includes(id)) ids.push(id);
  writeJson(session, SESSION_KEY, { ids });
};

export const disableAllHints = (userKey: string): void => {
  writeJson(local, storageKey(userKey), { ...getHintState(userKey), allOff: true });
};

export const resetHints = (userKey: string): void => {
  writeJson(local, storageKey(userKey), { dismissed: [], allOff: false });
  writeJson(session, SESSION_KEY, { ids: [] });
};
